import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CatalogProductDetails, CatalogProductDetailsVariant } from '@core/models/products';
import { ApiService } from '@core/services/api/api.service';
import { SessionService } from '@core/services/session/session.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DataLayerService } from '@core/services/seo/data-layer.service';
import { select, Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ContentfulService } from '@core/services/cms/contentful.service';
import { selectCatalogCurrentProductDetails, selectDesignToolAvialable, selectDesignToolMockups } from '@core/state/design-tool/design-tool.selectors';
import { CatalogDesignService } from '@core/services/catalog/catalog-design.service';
import { getProductVariantColor } from '@core/state/catalog/catalog.actions';
import { selectProductVariantColor } from '@core/state/catalog/catalog.selectors';
import { MetaService } from '@core/services/seo/meta-tag-service';
import { DesignToolAvailable, VariantColors, VariantImages } from '@core/models/designTool';
import { getDesignToolMockups } from '@core/state/design-tool/design-tool.actions';
import { SharedService } from '@shared/shared.service';
import { AuthService } from '@auth0/auth0-angular';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  public authenticated: boolean
  public product: CatalogProductDetails
  public destroyed$ = new Subject<void>()
  inventory: any;
  colorVariants = [];
  productDetails$: Observable<CatalogProductDetails> = this.store.select(selectCatalogCurrentProductDetails)
  productVariantColor$ : Observable<VariantColors[]>= this.store.pipe(select(selectProductVariantColor));
  designToolAvailableProducts$ = this.store.pipe(select(selectDesignToolAvialable));
  designMockups$: Observable<VariantImages> = this.store.select(selectDesignToolMockups);
  selectedVariant?: CatalogProductDetailsVariant|null;
  selectedImageUrl?: string|null;
  section1: any;
  section2: any;
  section3: any;
  productSectionName1: any;
  productSectionName2: any;
  productSectionName3: any;
  hasInventory: boolean;
  mockupImages: VariantImages;
  designToolAvailableProducts: DesignToolAvailable[];
  variants: VariantColors[];
  screenWidth: number;
  requestSubmitted: boolean;

  constructor(
    public activatedRoute: ActivatedRoute,
    protected apiService: ApiService,
    private sessionService: SessionService,
    protected breakpointService: BreakpointObserver,
    private router: Router,
    private dataLayerService: DataLayerService,
    private store: Store,
    protected authService: AuthService,
    private http: HttpClient,
    private contentfulService: ContentfulService,
    private sharedService: SharedService,
    private catalogDesignService: CatalogDesignService,
    private metaService: MetaService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.dataLayerService.logPageView(event.url)
    })
  }

  async ngOnInit(): Promise<void> {
    this.authenticated = await firstValueFrom(this.authService.isAuthenticated$);
    this.screenWidth = window.innerWidth;

    this.designMockups$.pipe(takeUntil(this.destroyed$)).subscribe((mockups: VariantImages) => {
      this.mockupImages = mockups
    })
    // Combing data from productVariantColor selctor and the designToolAvailableProducts selector
    /** 
     * Checking if the product is customizable
    */
    this.productDetails$.pipe(
      takeUntil(this.destroyed$),
      filter(resp => !!resp),
      tap((data: CatalogProductDetails) => {
        this.product = { ...data };
        this.product.description = this.product.description ?? '';
        this.product.variants.forEach((variant) => {
          const colorName = variant.colorName ?? variant['color'];
          const color = this.colorVariants.find((color) => color[colorName]);
          let size = variant['sizeName'] ?? variant.size ?? variant['sizes'];
          if (color) {
            color[colorName].push({ gtin: variant.gtin, sizeName: size });
          } else {
            this.colorVariants.push({ [colorName]: [{ gtin: variant.gtin, sizeName: size }] });
          }
        });
        this.product.variants = this.getUniqueVariants(JSON.parse(JSON.stringify(this.product.variants)));
        this.product['useImages'] = this.product.variants.every(el => el.images?.front?.length > 0)
        this.selectProductColor(this.product.variants[0]);
        this.metaService.updateMetaTags(
          `${this.product?.brand} ${this.product?.style} - ${this.product?.title}`,
          this.product?.description ? this.removeHtmlTags(this.product.description) : '',
          `Browse garments such as ${this.product?.brand} - ${this.product?.style} to customize for your brand on the Culture Studio Platform.`,
          this.selectedImageUrl,
        );
      }),
      switchMap(() => 
        combineLatest([
          this.productVariantColor$, 
          this.designToolAvailableProducts$
        ])
      ),
      filter(([variants, dtProducts]) => !!variants && !!dtProducts),
      takeUntil(this.destroyed$),
    ).subscribe(([variants, dtProducts]) => {
        this.designToolAvailableProducts = dtProducts
        this.variants = variants
    });

    // Subscribe to the observable for fetching Product Section 1 data
    this.contentfulService.getProductSection1().pipe(take(1)).subscribe(
      (data) => {
        // When data is received, assign it to the 'section1' property
        this.section1 = data;

        // Extract the 'sectionName' from the first item in the data and assign it
        this.productSectionName1 = this.section1 && this.section1[0] ? this.section1[0].sectionName : '';
      },
      (error) => {
        // Handle errors by logging them to the console
        console.error('Error:', error);
      }
    )
    
    // Subscribe to the observable for fetching Product Section 2 data
    this.contentfulService.getProductSection2().pipe(take(1)).subscribe(
      (data) => {
        // When data is received, assign it to the 'section2' property
        this.section2 = data;

        // Extract the 'sectionName' from the first item in the data and assign it
        this.productSectionName2 = this.section2 && this.section2[0] ? this.section2[0].sectionName : '';
      },
      (error) => {
        // Handle errors by logging them to the console
        console.error('Error:', error);
      }
    )
    
    // Subscribe to the observable for fetching Product Section 3 data
    this.contentfulService.getProductSection3().pipe(take(1)).subscribe(
      (data) => {
        // When data is received, assign it to the 'section3' property
        this.section3 = data;

        // Extract the 'sectionName' from the first item in the data and assign it
        this.productSectionName3 = this.section3 && this.section3[0] ? this.section3[0].sectionName : '';
      },
      (error) => {
        // Handle errors by logging them to the console
        console.error('Error:', error);
      }
    )
  }

  hasAvailableImages(): boolean {
    return this.mockupImages?.front?.length > 0
  }

  scrollRight(container) {
    this.animateScroll(container, 250);
  }

  scrollLeft(container) {
    this.animateScroll(container, -250);
  }

  animateScroll(container, distance) {
    const start = container.scrollLeft;
    const duration = 500; // in milliseconds
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const easedProgress = this.easeInOutQuad(progress);
      container.scrollLeft = start + (distance * easedProgress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  easeInOutQuad(progress) {
    progress /= 0.5;
    if (progress < 1) {
      return 0.5 * progress * progress;
    }
    progress--;
    return -0.5 * (progress * (progress - 2) - 1);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  pluckUniqueSizes(inputArray, colorName) {
    const uniqueObjects = {};
    inputArray[colorName].forEach(item => {
      const sizeName = item.sizeName;

      if (!uniqueObjects[sizeName]) {
        uniqueObjects[sizeName] = item;
      } else {
        // If an object with the same sizeName already exists, you can merge or update the existing object here.
        // For now, it will just keep the first occurrence.
      }
    });

    // Convert the uniqueObjects back to an array
    const resultArray = { [colorName]: Object.values(uniqueObjects) };
    return resultArray;
  }

  selectProductColor(variant?: CatalogProductDetailsVariant|null) {
    let colorName = variant?.colorName;
    // If color is changed & request is already submitted, then reset back to false
    if(this.requestSubmitted === true) {
      this.requestSubmitted = false;
    }
    
    if (this.sessionService.getActiveClient()) {
      this.store.dispatch(getProductVariantColor({ brand: this.product.brand, style: this.product.style, color: colorName, clientId: this.sessionService.getActiveClient().id, positions: [] }));
    }

    const product = {
      brand: this.product.brand,
      style: this.product.style,
      color: colorName
    }
    this.store.dispatch(getDesignToolMockups({product}))

    // need to find the product.colorName in my this.colorVariants and retrieve it
    let productColorData = this.colorVariants.find((color) => color[colorName]);
    // if(!productColorData) return; // or handle this scenario appropriately

    this.selectedVariant = variant;
    this.selectedImageUrl = variant?.images?.front

    // // now get all the gtins from the productColorData and ensure they are not null and are valid
    let productGtins = productColorData[colorName].filter(gtinObj => gtinObj && gtinObj.gtin).map(gtinObj => gtinObj.gtin);

    this.dataLayerService.logProductView(variant.brand, variant.style, variant);

    this.getInventoryMongo(colorName, productGtins, productColorData).subscribe((response) => {
      if (response) {
        this.inventory = this.pluckUniqueSizes(response, colorName);
        this.hasInventory = response && response[colorName] && response[colorName][0]?.totalQty > 0 ? true : false
      }
    },
      (error) => {
        console.error('Error getInventoryMongo() method: ', error);
      });
  }

  getInventoryMongo(color, ids, data) {
    const params = new HttpParams({ fromObject: { gtin: ids } })
    const url = `${environment.apiMicroservice}products/inventory`;
    return this.http.get(url, { params }).pipe(
      map((response: any) => {
        if (response && response.length > 0) {
          return {
            [color]: data[color].map(item => {
              const matchedData = response.find(dataItem => dataItem._id === item.gtin);
              return {
                ...item,
                totalQty: matchedData ? matchedData.totalQty : 0,
                lastUpdatedAt: matchedData ? matchedData.lastUpdatedAt : null,
              };
            }).sort((a, b) => {
              const sizeOrder = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
              let sizeA = a.sizeName ? a.sizeName : a.size ? a.size : a.sizes;
              let sizeB = b.sizeName ? b.sizeName : b.size ? b.size : b.sizes;
              const indexA = sizeOrder.indexOf(sizeA);
              const indexB = sizeOrder.indexOf(sizeB);

              // If both sizes are in the sizeOrder, use the indices for comparison
              if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
              }

              // If one size is in the sizeOrder and the other is not, put the latter at the end
              if (indexA === -1 && indexB !== -1) {
                return 1;
              }
              if (indexA !== -1 && indexB === -1) {
                return -1;
              }

              // If both sizes are not in the sizeOrder, use their natural order or consider them equal
              return sizeA.localeCompare(sizeB);
            })
          };
        } else {
          return null;
        }
      }));

  }

  removeHtmlTags(input: string): string {
    const regex = /(<([^>]+)>)/gi;
    return input.replace(regex, '');
  }

  /**
 * Returns an array of unique variants based on the colorName or color property.
 * @param variants Array of variant objects
 * @returns Array of unique variants
 */
  getUniqueVariants(variants: CatalogProductDetailsVariant[]): CatalogProductDetailsVariant[] {
    const uniqueVariants = [];
    const seenColors = new Set();
    
    for (const variant of variants) {
      if (!(variant?.images?.front?.length > 0)) {
        continue
      }
      const colorName = (variant.colorName || variant['color'])?.toLowerCase();
      this.formatVariantImages(variant, variant.vendorName)
      if (!seenColors.has(colorName) && !colorName.includes('(disc)')) {
        uniqueVariants.push(variant);
        seenColors.add(colorName);
      }
    }
    return uniqueVariants;
  }

  /**
   * Formats the URL for variant images based on the vendor.
   * @param variant Variant object
   * @param vendor Vendor string
   * @returns void
   */
  formatVariantImages(variant, vendor): void {
    if (!variant.images) {
      return
    }
    Object.keys(variant.images).forEach((imageKey: string) => { // Explicitly type 'image' as a string
      if (variant.images?.[imageKey]?.length > 0) {
        if (vendor?.includes?.('ssactivewear')) {
          variant.images[imageKey] = variant.images[imageKey].replace('/Images/Style', '');
        }
      }
    });
  }
  
  navigateToCustomize() {
    if (this.authenticated) {
      if (!this.selectedVariant) {
        this.openAlert('Please select a color!', 'alert-danger')
      } else {
        let queryParams = this.selectedVariant ? { queryParams: { color: this.selectedVariant.colorName } } : {};
        this.router.navigate([`/catalog/${this.product.brand}/${this.product.style}/customize`], queryParams);
      }
    } else {
      this.openAlert('Please login to customize this product', 'toast-info')
    }
  }

  async requestProductToDesignTool() {
    const url = `${environment.apiUrl2}api/v2/order/requestProduct`
    const user = await firstValueFrom(this.authService.user$)
    const request = {
      "product": this.product,
      "user": user,
      "client": this.sessionService.getActiveClient(),
      "color": this.selectedVariant?.colorName ? this.selectedVariant.colorName : ''
    }
    this.requestSubmitted = true;
    return firstValueFrom(this.http.post(url, request));
  }

  openAlert(prompt, type) {
    this.sharedService.showNotification({ data: { title: prompt, class: type }, autohide: true });
  }

  trackSizeByFn(size) {
    return size.sizeName
  }

  selectCurrentImage(imageUrl: string) {
    this.selectedImageUrl = imageUrl
  }

  redirectLogin(){
    this.authService.loginWithRedirect({
      openUrl: (url) => {
        this.sessionService.flushUser();
        window.location.replace(url);
      },
      appState: {
        redirect: `/catalog/${this.product.brand}/${this.product.style}/customize?color=${this.selectedVariant?.colorName}&guest=0`
      }
    });
  }

  get isCustomizablePublic(): boolean {
    return this.hasAvailableImages() && !!this.catalogDesignService.isProductCustomizable(this.product?.brand, this.product?.style, this.selectedVariant?.colorName, this.designToolAvailableProducts); // returns a boolean
  }

  get isCustomizable(): boolean {
    const value = this.variants?.length > 0 && this.hasAvailableImages() && this.product?.brand && this.product?.style && !!this.catalogDesignService.isProductCustomizable(this.product?.brand, this.product?.style, this.selectedVariant.colorName, this.designToolAvailableProducts)
    return value;
  }
}
